/* jshint esversion: 6 */
/* jshint esversion: 9 */
/* jshint ignore:start */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MyContext } from '../Context/MyProvider';
import axios from 'axios';
import RazorPay from './RazorPay';
import { showToast } from '../showTost';
import { useTranslation } from 'react-i18next';
import Stripe from './Stripe';
import Cashfree from './Cashfree';
import secureLocalStorage from "react-secure-storage";
import { applyImageFallback, buildImageUrl } from '../utils/image';

export const ImgLoad = ({ url, style, className = "" }) => {
    const [imgLoaded, setImgLoaded] = useState(false);
    const isStyleObj = style && typeof style === "object" && !Array.isArray(style);
    return (
        <img
            src={url}
            alt="Payment method"
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            onError={(e) => {
                applyImageFallback(e, process.env.REACT_APP_IMG_BASE_URL || process.env.REACT_APP_API_BASE_URL || "https://portal.rapidmedik.com", "payment");
                setImgLoaded(true);
            }}
            style={isStyleObj ? { objectFit: "contain", ...style } : { objectFit: "contain" }}
            className={`shrink-0 object-contain ${!isStyleObj && typeof style === "string" ? style : ""} ${className} ${imgLoaded ? "opacity-100" : "opacity-0"}`}
        />
    );
};

/**
 * Payment gateway picker + checkout.
 * With a single available gateway, checkout opens immediately — no second
 * "Continue" click after the booking form. Multiple gateways show a provider picker.
 */
const Payment = ({ Amount, Data, INDEX, photos, did }) => {

    const { t } = useTranslation();

    const { ApiBaseUrl, ImgBaseUrl, setPaymentStatus, apiloading } = useContext(MyContext);
    const [paymentList, setPaymentList] = useState([]);
    const [selectPaymentId, setSelectPaymentId] = useState();
    const [isShow, setIsShow] = useState(false);
    const [attribute, setAttribute] = useState("");
    const [listReady, setListReady] = useState(false);
    const startedRef = useRef(false);

    const visiblePayments = useMemo(
        () => (paymentList || []).filter((item) => [1, 3, 11].includes(Number(item.id)) && Number(item.id) !== Number(INDEX)),
        [paymentList, INDEX]
    );

    const shouldAutoStart = visiblePayments.length === 1;

    const startCheckout = useCallback((paymentId, attr) => {
        const id = Number(paymentId);
        if (!id) {
            showToast({ title: t("Select Payment Method"), id: "error" });
            return false;
        }

        sessionStorage.setItem("Paymentid", String(id));

        const Userid = secureLocalStorage.getItem("Userid");
        if (!Userid) {
            showToast({ title: t("Please login to continue"), id: "error" });
            return false;
        }

        if (Data) {
            sessionStorage.setItem("PaymentData", JSON.stringify({
                ...Data,
                status: "1",
                payment_id: id
            }));
        }

        const hasPayload = Boolean(Data || sessionStorage.getItem("PaymentData"));
        if (!hasPayload && id !== 16) {
            showToast({ title: t("Payment data missing. Please try again."), id: "error" });
            return false;
        }

        if (id === 16) {
            setPaymentStatus("PaymentDone");
            return true;
        }

        if (Data && photos && did) {
            const Userid = secureLocalStorage.getItem("Userid");
            if (Userid) {
                const formData = new FormData();
                formData.append("uid", Userid);
                formData.append("doctor_id", did);
                photos.forEach((photo) => {
                    formData.append("image", photo);
                });
                axios.post(`${ApiBaseUrl}/customer/add_patient_prescription`, formData)
                    .catch((error) => {
                        console.error(error);
                    });
            }
        }

        setIsShow(true);
        return true;
    }, [ApiBaseUrl, Data, did, photos, setPaymentStatus, t]);

    useEffect(() => {
        axios.get(`${ApiBaseUrl}/customer/payment_list`)
            .then((res) => {
                const list = res.data.payment_list || [];
                setPaymentList(list);
                const visible = list.filter((item) => Number(item.id) !== Number(INDEX));
                if (visible.length === 1) {
                    setSelectPaymentId(Number(visible[0].id));
                    setAttribute(visible[0].attribute || "");
                } else if (visible.length > 1) {
                    const preferred = visible.find((p) => Number(p.id) === 11) || visible[0];
                    setSelectPaymentId(Number(preferred.id));
                    setAttribute(preferred.attribute || "");
                }
                setListReady(true);
            })
            .catch((error) => {
                console.error(error);
                setListReady(true);
            });
    }, [ApiBaseUrl, INDEX]);

    // One gateway (or forced autoStart) → open checkout immediately, no second click
    useEffect(() => {
        if (!listReady || isShow || startedRef.current) return;
        if (!shouldAutoStart || !selectPaymentId) return;
        startedRef.current = true;
        startCheckout(selectPaymentId, attribute);
    }, [listReady, shouldAutoStart, selectPaymentId, attribute, isShow, startCheckout]);

    const SelectPayment = (item) => {
        setSelectPaymentId(Number(item.id));
        setAttribute(item.attribute);
    };

    const HandleContinue = () => {
        startCheckout(selectPaymentId, attribute);
    };

    // Once checkout is open with a single gateway, skip the picker UI entirely
    const hidePicker = isShow && (shouldAutoStart || visiblePayments.length === 1);

    return (
        <div>
            {!hidePicker && (
                <>
                    <div className={`${visiblePayments.length <= 2 ? "max-h-none" : "max-h-[320px] overflow-y-auto"} scroll-container`}>
                        {listReady && visiblePayments.length === 0 && (
                            <p className="text-sm text-gray-500 py-4 text-center">No payment methods available</p>
                        )}
                        {!listReady && (
                            <p className="text-sm text-gray-500 py-4 text-center">Loading payment…</p>
                        )}
                        {visiblePayments.map((item) => (
                            <div
                                onClick={() => SelectPayment(item)}
                                key={item.id}
                                className={`payment-box border-[1px] ${Number(selectPaymentId) === Number(item.id) ? "border-[#0d9488] bg-orange-50/40" : "border-[#8080803b]"}`}
                            >
                                <div className="flex items-center gap-[12px] min-w-0 flex-1">
                                    <ImgLoad
                                        url={buildImageUrl(ImgBaseUrl, item.image, "payment")}
                                        style={{ width: 56, height: 56, border: "1px solid #d1d5db", borderRadius: 10, background: "#fff" }}
                                        className="w-14 h-14"
                                    />
                                    <div className="min-w-0">
                                        <h1 className='font-[500] text-[16px] sm:text-[18px] truncate'>{item.name}</h1>
                                        {item.sub_title ? (
                                            <p className='font-[400] leading-[18px] text-sm text-gray-600 line-clamp-2'>{item.sub_title}</p>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="w-[30px] check-box shrink-0">
                                    <input
                                        className="peer hidden"
                                        type="radio"
                                        name="payment_gateway"
                                        checked={Number(selectPaymentId) === Number(item.id)}
                                        onChange={() => SelectPayment(item)}
                                    />
                                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${Number(selectPaymentId) === Number(item.id) ? "box-activeborder" : "box-border"}`}>
                                        <span className={`w-[10px] h-[10px] rounded-full duration-[0.3s] ${Number(selectPaymentId) === Number(item.id) && "box-activebg"}`}></span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {!shouldAutoStart && (
                        <button onClick={HandleContinue} className='payment-btn mt-[12px]'>
                            {apiloading
                                ? <div className="loader mx-auto"></div>
                                : <span>{t("Continue")}</span>}
                        </button>
                    )}
                    {shouldAutoStart && !isShow && listReady && (
                        <p className="text-sm text-center text-gray-500 py-4">Opening payment…</p>
                    )}
                </>
            )}

            {(Number(selectPaymentId) === 1 && isShow) && <RazorPay Amount={Amount} Attribute={attribute} />}
            {(Number(selectPaymentId) === 3 && isShow) && <Stripe Amount={Amount} />}
            {(Number(selectPaymentId) === 11 && isShow) && <Cashfree Amount={Amount} />}
        </div>
    );
}

export default Payment
/* jshint ignore:end */
